import React from 'react';
import Modal from 'react-modal';
import SubmitButton from './SubmitButton';

interface HowItWorksModalProps {
  show: boolean;
  close: () => void;
  storageRequired: boolean;
}

const customStyles = {
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxWidth: '900px',
    maxHeight: '80vh',
    overflow: 'auto',
    alignItems: 'center',
  },
};

export function HowItWorksModal({
  show,
  close,
  storageRequired,
}: HowItWorksModalProps): JSX.Element {
  const steps = storageRequired
    ? [
        {
          title: 'Book Your Storage',
          description:
            'Pick a date when you want us to collect your belongings. Choose your boxes and packing materials online.',
          icon: '📅',
          bgColor: '#f3f4f6',
        },
        {
          title: "We'll Collect Your Boxes",
          description:
            'On your chosen date, our friendly drivers will collect your packed boxes from your address.',
          icon: '🚚',
          bgColor: '#e0e7ff',
        },
        {
          title: 'Stored Securely',
          description:
            "We'll store your boxes in our secure, monitored warehouses with 24/7 security.",
          icon: '🏢',
          bgColor: '#ddd6fe',
        },
        {
          title: 'Ready When You Need',
          description:
            'Access your items anytime through our online portal. Schedule redelivery whenever you need your belongings back.',
          icon: '📦',
          bgColor: '#fef3c7',
        },
      ]
    : [
        {
          title: 'Book Your Shipment',
          description:
            'Select your boxes and packing materials online. Choose collection and delivery addresses for your items.',
          icon: '📅',
          bgColor: '#f3f4f6',
        },
        {
          title: "We'll Collect Your Boxes",
          description:
            'Our drivers will collect your packed boxes from your specified address on your chosen date.',
          icon: '🚚',
          bgColor: '#e0e7ff',
        },
        {
          title: 'Safe Transportation',
          description:
            'Your boxes are transported safely and securely to the destination address you specified.',
          icon: '🛣️',
          bgColor: '#ddd6fe',
        },
        {
          title: 'Delivered to Destination',
          description:
            'We deliver your boxes directly to your chosen delivery address. Track your shipment through our portal.',
          icon: '🏠',
          bgColor: '#fef3c7',
        },
      ];

  return (
    <Modal
      isOpen={show}
      onRequestClose={close}
      style={customStyles}
      contentLabel="How It Works Modal"
    >
      <div className="how-it-works-modal">
        <div className="modal-header">
          <h2>How {storageRequired ? 'Storage' : 'Shipping'} Works</h2>
          <SubmitButton red={true} onClick={close}>
            <i className="fas fa-times" />
          </SubmitButton>
        </div>

        <div className="steps-container">
          {steps.map((step, index) => (
            <div
              key={index}
              className="step-item"
              style={{ backgroundColor: step.bgColor }}
            >
              <div className="step-icon">
                <span>{step.icon}</span>
                <div className="step-number">{index + 1}</div>
              </div>
              <div className="step-content">
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <SubmitButton onClick={close}>Got it!</SubmitButton>
        </div>
      </div>
    </Modal>
  );
}
